import { intersection, isEmpty } from "lodash";
import React, { useCallback, useContext, useMemo, useState } from "react";

type ContextValue = {
  addLoader: (loadKey: string) => void;
  removeLoader: (loadKey: string) => void;
  getIsReady: (loaders?: string[]) => boolean;
};

const LoadingContext = React.createContext<ContextValue | undefined>(undefined);

export enum LoadingKey {
  workspaceWallets = "workspace-wallets",
  workspaceWalletHotspotCount = "workspace-wallets",
  workspaceLabels = "workspace-labels",
  workspaceNotificationSubscriptions = "workspace-notifications",
  workspaceHotspots = "workspace-hotspots",
  workspaceLocations = "workspace-locations",
  workspacePlacements = "workspace-placements",
  workspaceContacts = "workspace-contacts",
  workspaceInstalls = "workspace-installs",
  workspaceCommissionTemplates = "workspace-commission-templates",
  workspaceCommissionReports = "workspace-commission-reports",
  discordChannels = "discord-channels",
  hotspotsAroundLocations = "hotspots-around-locations",
  savingPlacement = "saving-placement",
  paymentTransactions = "payment-transactions",
  hotspotMakers = "hotspot-makers"
}

interface Props {}

const LoadingProvider: React.FC<Props> = (props) => {
  const [loaders, setLoaders] = useState<string[]>([
    LoadingKey.workspaceWallets,
    LoadingKey.workspaceLabels,
    LoadingKey.workspaceNotificationSubscriptions,
    LoadingKey.workspaceLocations,
    LoadingKey.workspacePlacements,
    LoadingKey.workspaceContacts,
    LoadingKey.workspaceInstalls,
    LoadingKey.workspaceCommissionTemplates,
    LoadingKey.workspaceCommissionReports,
    LoadingKey.hotspotMakers
  ]);

  const addLoader = useCallback((loadKey: string) => {
    setLoaders((loaders: string[]) => {
      if (loaders.includes(loadKey)) {
        return loaders;
      }
      return [...loaders, loadKey];
    });
  }, []);

  const removeLoader = useCallback((loadKey: string) => {
    setLoaders((loaders: string[]) => {
      if (loaders.includes(loadKey)) {
        return loaders.filter((l) => l !== loadKey);
      }
      return loaders;
    });
  }, []);

  const getIsReady = useCallback(
    (whitelistedLoaders?: string[]) =>
      whitelistedLoaders != null
        ? isEmpty(intersection(whitelistedLoaders, loaders))
        : isEmpty(loaders),
    [loaders]
  );

  const value = useMemo(
    () => ({
      addLoader,
      removeLoader,
      getIsReady
    }),
    [addLoader, getIsReady, removeLoader]
  );

  return <LoadingContext.Provider value={value} {...props} />;
};

const useLoading = (): ContextValue => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoading must be used within an LoadingProvider");
  }
  return context;
};

export { useLoading, LoadingProvider };
